import { Statuses } from '@geee-be/core';
import type { PaginatedList, Vehicle } from '@local/common-lib';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import { VehicleListView } from './list.view.js';

export const VehicleListPage: FC = () => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [vehicles, setVehicles] = useState<Vehicle.Type[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle.Type | null>(
    null,
  );

  useSetTitle('Vehicle Register');

  const loadVehicles = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await auth.axios.get<PaginatedList<Vehicle.Type>>(
        '/api/v1/my/organization/vehicles',
      );
      setVehicles(data.items);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === Statuses.NOT_FOUND) {
        setVehicles([]);
      } else {
        enqueueSnackbar('Error loading vehicles', { variant: 'error' });
        Sentry.captureException(error);
      }
    } finally {
      setLoading(false);
    }
  }, [auth.axios, enqueueSnackbar]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const handleDeleteClick = (vehicle: Vehicle.Type) => {
    setVehicleToDelete(vehicle);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!vehicleToDelete) return;
    try {
      await auth.axios.delete(
        `/api/v1/my/organization/vehicles/${encodeURIComponent(vehicleToDelete.rego)}`,
      );
      enqueueSnackbar('Vehicle removed', { variant: 'success' });
      await loadVehicles();
    } catch (error) {
      enqueueSnackbar('Error removing vehicle', { variant: 'error' });
      Sentry.captureException(error);
    } finally {
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
    }
  };

  const handleEditClick = (vehicle: Vehicle.Type) => {
    navigate(`/vehicle/edit/${encodeURIComponent(vehicle.rego)}`);
  };

  if (loading) {
    return <MiddleSpinner />;
  }

  return (
    <>
      <VehicleListView
        onAddClick={() => navigate('/vehicle/new')}
        onDeleteClick={handleDeleteClick}
        onEditClick={handleEditClick}
        vehicles={vehicles}
      />
      <Dialog
        aria-describedby="delete-vehicle-description"
        aria-labelledby="delete-vehicle-title"
        maxWidth="sm"
        onClose={() => setDeleteDialogOpen(false)}
        open={deleteDialogOpen}
      >
        <DialogTitle id="delete-vehicle-title">
          Remove Vehicle {vehicleToDelete?.rego}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-vehicle-description">
            This will remove the vehicle from the register. It can be added back
            later if needed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            autoFocus
            color="error"
            onClick={handleConfirmDelete}
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
